import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useClientView } from "../contexts/ClientViewContext";
import { useTheme } from "../contexts/ThemeContext";
import { useDatabase } from "../contexts/DatabaseContext";
import {
  Card,
  StatCard,
  LoadingSpinner,
  Grid2,
  Grid3,
  Grid4,
  MetricGrid,
  ProgressBar,
  MetricWithTooltip,
  IconButton,
  DatabaseSelector,
} from "../components/ui";
import { ArrowsRightLeftIcon, PencilSquareIcon, UserCircleIcon } from '@heroicons/react/24/outline';
import {
  useCompanies,
  useCompanyComparisons,
  useIndustryAnalytics,
  useRegionAnalytics,
  useVillageAnalytics,
  useBreakdown
} from "../hooks/useAnalytics";
import BreakdownView from "../components/BreakdownView";
import PercentileBarChart from "../components/charts/PercentileBarChart";
import MetricRadarChart from "../components/charts/MetricRadarChart";
import DistributionChart from "../components/charts/DistributionChart";
import MaxDiffChartsSection from "../components/charts/MaxDiffChartsSection";
import PDFExport from "../components/PDFExport";

// Legacy definitions (used when no database schema is available)
const legacyMetricDefinitions = {
  adoptionLevel:
    "Average self-reported usage of AI tools by employees on a scale of 0-100.",
  adoptionRate:
    "Percentage of employees who have used at least one AI tool in their work.",
  aiKnowledge:
    "Average score on the company-wide AI literacy quiz (out of 5).",
  learningInterest:
    "Percentage of employees who expressed active interest in AI training.",
  atiScore:
    "AI-Technology Interaction (ATI) score, measuring overall affinity and positive interaction with technology (0-100).",
  optimismScore:
    "Average sentiment towards AI's future impact on the company and jobs (0-10).",
  aiTrust:
    "Average self-reported trust in the accuracy and reliability of AI systems (0-10).",
  aiFear:
    "Average self-reported concern or fear about AI's negative impacts (0-10).",
};

function formatMetricValue(value, metricDef) {
  if (value == null || isNaN(value)) return '—';
  const decimals = metricDef?.decimals ?? 1;
  const formatted = Number(value).toFixed(decimals);
  const unit = metricDef?.unit || '';
  if (unit === '%') return `${formatted}%`;
  return `${formatted}${unit}`;
}

export default function Insights() {
  const { permissions } = useAuth();
  const { clientView } = useClientView();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const { databaseId, schema } = useDatabase();

  // Dynamic mode detection
  const isDynamic = !!databaseId && !!schema;
  const schemaMetrics = schema?.metrics || [];
  const layout = schema?.dashboardLayout || null;

  // Metric lookup from schema
  const metricLookup = useMemo(() => {
    const map = {};
    schemaMetrics.forEach(m => { map[m.key] = m; });
    return map;
  }, [schemaMetrics]);

  // Metric definitions (dynamic or legacy)
  const metricDefinitions = useMemo(() => {
    if (!isDynamic) return legacyMetricDefinitions;
    const defs = { ...legacyMetricDefinitions };
    schemaMetrics.forEach(m => {
      defs[m.key] = m.label + (m.unit ? ` (${m.unit})` : '');
    });
    return defs;
  }, [isDynamic, schemaMetrics]);

  // Helper to read metric from company (dynamic vs legacy)
  const getCompanyMetric = (company, key) => {
    if (isDynamic && databaseId) {
      return company?.databases?.[databaseId]?.metrics?.[key] ?? company?.metrics?.[key] ?? 0;
    }
    return company?.metrics?.[key] ?? 0;
  };

  // Main tab state
  const [mainTab, setMainTab] = useState("company");

  // Company tab state
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [activeTab, setActiveTab] = useState("all");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIndustryFilter, setSelectedIndustryFilter] = useState("All");
  const [selectedRegionFilter, setSelectedRegionFilter] = useState("All");

  // Breakdown tab states
  const [selectedIndustry, setSelectedIndustry] = useState("");
  const [selectedVillage, setSelectedVillage] = useState("");
  const [selectedRegion, setSelectedRegion] = useState("");
  const [selectedSector, setSelectedSector] = useState("");
  const [selectedMinistry, setSelectedMinistry] = useState("");

  // Ref to prevent effect 2 (state → URL) from overwriting URL-initiated navigations
  const urlSyncRef = useRef(false);

  // Use cached companies data (pass databaseId)
  const { data: allCompanies = [], isLoading: companiesLoading, isError: companiesError } = useCompanies({ limit: 200, databaseId });

  // Auto-select company when in client view mode
  useEffect(() => {
    if (clientView?.id && allCompanies.length > 0 && !selectedCompany) {
      const company = allCompanies.find(c => c.id === clientView.id);
      if (company) setSelectedCompany(company);
    }
  }, [clientView, allCompanies, selectedCompany]);

  // Use cached comparison data (for company tab) — pass databaseId
  const {
    allCompaniesComparison,
    industryComparison,
    villageComparison,
    regionComparison,
    sectorComparison,
    ministryComparison,
    isLoading: comparisonLoading,
    isError: comparisonError,
  } = useCompanyComparisons(selectedCompany?.id, databaseId);

  // Get category lists for breakdown tabs
  const { data: industryAnalytics = [] } = useIndustryAnalytics(databaseId);
  const { data: villageAnalytics = [] } = useVillageAnalytics(databaseId);
  const { data: regionAnalytics = [] } = useRegionAnalytics(databaseId);

  // Extract category names
  const industryList = industryAnalytics.map(item => item.industry).filter(i => i !== 'Unknown');
  const villageList = villageAnalytics.map(item => item.village).filter(v => v !== 'Unknown');
  const regionList = regionAnalytics.map(item => item.region).filter(r => r !== 'Unknown');
  const sectorList = ['Government', 'Non-Government'];
  const ministryList = [...new Set(
    allCompanies
      .filter(c => c.isGovernment && c.ministry && c.ministry !== 'Unknown')
      .map(c => c.ministry)
  )].sort();

  // Breakdown data hooks — pass databaseId
  const { data: industryBreakdown, isLoading: industryLoading, isError: industryError } = useBreakdown('industry', selectedIndustry, databaseId);
  const { data: villageBreakdown, isLoading: villageLoading, isError: villageError } = useBreakdown('village', selectedVillage, databaseId);
  const { data: regionBreakdown, isLoading: regionLoading, isError: regionError } = useBreakdown('region', selectedRegion, databaseId);
  const { data: sectorBreakdown, isLoading: sectorLoading, isError: sectorError } = useBreakdown('sector', selectedSector, databaseId);
  const { data: ministryBreakdown, isLoading: ministryLoading, isError: ministryError } = useBreakdown('ministry', selectedMinistry, databaseId);

  // Combine loading and error states
  const loading = comparisonLoading;
  const isLoadingCompanies = companiesLoading;
  const error = companiesError || comparisonError ? "Failed to load data" : null;

  // Check permissions on mount
  useEffect(() => {
    if (permissions) {
      const hasAccess = !permissions.routes || permissions.routes.length === 0 || permissions.routes.includes('/insights');
      if (!hasAccess) {
        navigate('/');
        return;
      }
    }
  }, [permissions, navigate]);

  // Initialize state from URL parameters
  useEffect(() => {
    if (!allCompanies.length) return;

    const view = searchParams.get('view') || 'company';
    const companyId = searchParams.get('id');
    const tab = searchParams.get('tab');
    const category = searchParams.get('category');

    let updated = false;

    if (['company', 'industry', 'village', 'region', 'sector', 'ministry'].includes(view)) {
      setMainTab(view);
    }

    if (view === 'company' && companyId) {
      const company = allCompanies.find(c => c.id === companyId);
      if (company) {
        setSelectedCompany(company);
        setIsDropdownOpen(false);
        updated = true;
        if (tab && ['all', 'industry', 'village', 'region', 'sector', 'ministry'].includes(tab)) {
          setActiveTab(tab);
        }
      }
    }

    if (category) {
      if (view === 'industry') setSelectedIndustry(category);
      if (view === 'village') setSelectedVillage(category);
      if (view === 'region') setSelectedRegion(category);
      if (view === 'sector') setSelectedSector(category);
      if (view === 'ministry') setSelectedMinistry(category);
    }

    if (updated) urlSyncRef.current = true;
  }, [allCompanies, searchParams]);

  // Update URL when state changes
  useEffect(() => {
    if (urlSyncRef.current) {
      urlSyncRef.current = false;
      return;
    }

    const params = new URLSearchParams();
    params.set('view', mainTab);

    if (mainTab === 'company') {
      if (selectedCompany) {
        params.set('id', selectedCompany.id);
        if (activeTab !== 'all') {
          params.set('tab', activeTab);
        }
      }
    } else {
      if (mainTab === 'industry' && selectedIndustry) params.set('category', selectedIndustry);
      if (mainTab === 'village' && selectedVillage) params.set('category', selectedVillage);
      if (mainTab === 'region' && selectedRegion) params.set('category', selectedRegion);
      if (mainTab === 'sector' && selectedSector) params.set('category', selectedSector);
      if (mainTab === 'ministry' && selectedMinistry) params.set('category', selectedMinistry);
    }

    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainTab, selectedCompany, activeTab, selectedIndustry, selectedVillage, selectedRegion, selectedSector, selectedMinistry]);

  // Get unique industries and regions for filters
  const industries = ["All", ...new Set(allCompanies.map((c) => c.industry))];
  const regions = ["All", ...new Set(allCompanies.map((c) => c.region))];

  // Filter companies based on search and filters, sorted alphabetically
  const filteredCompanies = allCompanies
    .filter((company) => {
      const matchesSearch = company.name
        .toLowerCase()
        .includes(searchQuery.toLowerCase());
      const matchesIndustry =
        selectedIndustryFilter === "All" ||
        company.industry === selectedIndustryFilter;
      const matchesRegion =
        selectedRegionFilter === "All" ||
        company.region === selectedRegionFilter;
      return matchesSearch && matchesIndustry && matchesRegion;
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  const handleSelectCompany = (company) => {
    setSelectedCompany(company);
    setIsDropdownOpen(false);
  };

  const handleReopen = () => {
    setIsDropdownOpen(true);
    setSearchQuery("");
    setSelectedIndustryFilter("All");
    setSelectedRegionFilter("All");
    setSelectedCompany(null);
    setActiveTab("all");
  };

  const handleCompanyClickFromBreakdown = (company) => {
    setMainTab('company');
    setSelectedCompany(company);
    setIsDropdownOpen(false);
    setActiveTab('all');
  };

  const getBgColorFromTextColor = (textColorClass) => {
    if (!textColorClass) return "bg-blue-500";
    return textColorClass.replace("text-", "bg-");
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat("en-US").format(value);
  };

  const getPerformanceColor = (difference) => {
    if (difference > 0) return "text-success dark:text-success";
    if (difference < 0) return "text-danger dark:text-danger";
  };

  const getPercentileColor = (percentile) => {
    if (percentile >= 75) return (theme === 'dark') ? "text-success" : "text-success";
    if (percentile >= 25) return (theme === 'dark') ? "text-text-muted" : "text-text-muted";
    return (theme === 'dark') ? "text-danger dark:text-danger-dark" : "text-danger";
  };

  const getRankLabel = (ranking) => {
    if (ranking?.rank === 1) return 'Top Performer';
    return `Top ${(100 - (ranking?.percentile ?? 0)).toFixed(0)}%`;
  };

  // Get the active comparison data based on selected tab
  const getComparisonData = () => {
    switch (activeTab) {
      case 'all': return allCompaniesComparison;
      case 'industry': return industryComparison;
      case 'village': return villageComparison;
      case 'region': return regionComparison;
      case 'sector': return sectorComparison;
      case 'ministry': return ministryComparison;
      default: return allCompaniesComparison;
    }
  };

  const comparisonData = getComparisonData();

  // Filter companies for distribution charts based on active tab
  const filteredCompaniesForDistribution = useMemo(() => {
    if (!allCompanies || allCompanies.length === 0 || !selectedCompany) return [];

    switch (activeTab) {
      case 'industry':
        return allCompanies.filter(c => c.industry === selectedCompany.industry);
      case 'village':
        return allCompanies.filter(c => c.village === selectedCompany.village);
      case 'region':
        return allCompanies.filter(c => c.region === selectedCompany.region);
      case 'sector':
        return allCompanies.filter(c => c.isGovernment === selectedCompany.isGovernment);
      case 'ministry':
        return allCompanies.filter(c => c.ministry === selectedCompany.ministry && c.isGovernment);
      case 'all':
      default:
        return allCompanies;
    }
  }, [allCompanies, selectedCompany, activeTab]);

  // Dynamic ranking cards (first 4 metrics from comparison layout or schema)
  const rankingMetrics = useMemo(() => {
    if (!isDynamic) return null;
    const keys = layout?.comparisonMetrics || schemaMetrics.map(m => m.key);
    return keys.slice(0, 4).map(key => metricLookup[key]).filter(Boolean);
  }, [isDynamic, layout, schemaMetrics, metricLookup]);

  // Dynamic radar chart metrics
  const radarMetrics = useMemo(() => {
    if (!isDynamic) return null;
    const keys = layout?.radarMetrics || schemaMetrics.slice(0, 6).map(m => m.key);
    return keys.map(key => metricLookup[key]).filter(Boolean);
  }, [isDynamic, layout, schemaMetrics, metricLookup]);

  // Render dynamic ranking cards
  const renderDynamicRankingCards = () => {
    if (!rankingMetrics || !comparisonData?.rankings) return null;
    return (
      <Grid4 className="mb-6">
        {rankingMetrics.map(metric => {
          const ranking = comparisonData.rankings[metric.key];
          if (!ranking) return null;
          return (
            <StatCard
              key={metric.key}
              title={`${metric.label} Rank`}
              value={`#${ranking.rank}`}
              valueColor={getPercentileColor(ranking.percentile)}
              subtitle={
                <>
                  <span>{getRankLabel(ranking)}</span>
                  <ProgressBar
                    value={ranking.percentile}
                    colorClass={getBgColorFromTextColor(
                      getPercentileColor(ranking.percentile)
                    )}
                  />
                </>
              }
            />
          );
        })}
      </Grid4>
    );
  };

  // Render dynamic metric comparison grids
  const renderDynamicMetricGrids = () => {
    if (!comparisonData?.differences) return null;
    const allKeys = schemaMetrics.map(m => m.key);
    const mid = Math.ceil(allKeys.length / 2);
    const col1Keys = allKeys.slice(0, mid);
    const col2Keys = allKeys.slice(mid);

    const buildMetrics = (keys) => keys.map(key => {
      const def = metricLookup[key];
      const targetVal = getCompanyMetric(comparisonData.targetCompany, key);
      const diff = comparisonData.differences[key] || 0;
      return {
        name: <MetricWithTooltip label={def?.label || key} definition={metricDefinitions[key] || def?.label || key} />,
        value: formatMetricValue(targetVal, def),
        difference: `${diff > 0 ? '+' : ''}${Number(diff).toFixed(def?.decimals ?? 1)}`,
        differenceColor: getPerformanceColor(diff),
      };
    });

    return (
      <Grid2 className="mb-6">
        {col1Keys.length > 0 && (
          <Card>
            <h3 className="text-xl font-bold text-text-primary-light dark:text-text-primary mb-4">
              Key Metrics
            </h3>
            <MetricGrid metrics={buildMetrics(col1Keys)} />
          </Card>
        )}
        {col2Keys.length > 0 && (
          <Card>
            <h3 className="text-xl font-bold text-text-primary-light dark:text-text-primary mb-4">
              Additional Metrics
            </h3>
            <MetricGrid metrics={buildMetrics(col2Keys)} />
          </Card>
        )}
      </Grid2>
    );
  };

  // Render dynamic visual analytics
  const renderDynamicCharts = () => {
    if (!comparisonData?.rankings || !comparisonData?.averages) return null;

    // Percentile bar chart: first 4 metrics
    const percentileMetrics = (rankingMetrics || []).map(m => ({
      label: m.label,
      percentile: comparisonData.rankings[m.key]?.percentile || 0,
      value: getCompanyMetric(selectedCompany, m.key),
    }));

    // Radar chart metrics
    const radarData = (radarMetrics || schemaMetrics.slice(0, 6)).map(m => {
      const def = typeof m === 'object' ? m : metricLookup[m];
      if (!def) return null;
      const companyVal = getCompanyMetric(selectedCompany, def.key);
      const avgVal = comparisonData.averages[def.key] || 0;
      // Normalize to 0-100 range for radar
      const scale = def.displayScale || {};
      const max = scale.max || 100;
      return {
        metric: def.label,
        company: max <= 10 ? companyVal * (100 / max) : companyVal,
        comparison: max <= 10 ? avgVal * (100 / max) : avgVal,
      };
    }).filter(Boolean);

    const comparisonLabel = activeTab === 'all' ? 'All Companies' :
      activeTab === 'industry' ? selectedCompany.industry :
      activeTab === 'village' ? selectedCompany.village :
      activeTab === 'region' ? selectedCompany.region : 'Average';

    return (
      <div className="mb-6" id="insights-content">
        <h3 className="text-xl font-bold text-text-primary-light dark:text-text-primary mb-4">
          Visual Analytics
        </h3>
        <Grid2>
          <PercentileBarChart
            title="Performance Percentiles"
            data={percentileMetrics}
          />
          <MetricRadarChart
            title="Metric Comparison"
            companyLabel={selectedCompany.name}
            comparisonLabel={comparisonLabel}
            data={radarData}
          />
        </Grid2>
      </div>
    );
  };

  // Render dynamic distribution charts
  const renderDynamicDistributions = () => {
    if (!comparisonData || filteredCompaniesForDistribution.length === 0) return null;
    const distMetrics = (rankingMetrics || schemaMetrics.slice(0, 4)).map(m => {
      const def = typeof m === 'object' ? m : metricLookup[m];
      if (!def) return null;
      const metricPath = isDynamic && databaseId
        ? `databases.${databaseId}.metrics.${def.key}`
        : `metrics.${def.key}`;
      const scale = def.displayScale || {};
      const max = scale.max || 100;
      return { def, metricPath, max };
    }).filter(Boolean);

    return (
      <div className="mb-6">
        <h3 className="text-xl font-bold text-text-primary-light dark:text-text-primary mb-4">
          Distribution Analysis
        </h3>
        <p className="text-sm text-text-secondary-light dark:text-text-secondary mb-4">
          See where your company stands in the distribution of all{' '}
          {activeTab === 'all' ? 'companies' :
           activeTab === 'industry' ? `companies in ${selectedCompany.industry}` :
           activeTab === 'village' ? `companies in ${selectedCompany.village}` :
           activeTab === 'region' ? `companies in ${selectedCompany.region}` :
           activeTab === 'sector' ? `${selectedCompany.isGovernment ? 'government' : 'non-government'} companies` :
           activeTab === 'ministry' ? `companies in ${selectedCompany.ministry}` : 'companies'}
          {' '}({filteredCompaniesForDistribution.length} total)
        </p>
        <Grid2 className="gap-6">
          {distMetrics.map(({ def, metricPath, max }) => (
            <Card key={def.key}>
              <DistributionChart
                title={`${def.label} Distribution`}
                companyValue={getCompanyMetric(selectedCompany, def.key)}
                allCompanies={filteredCompaniesForDistribution}
                metricKey={metricPath}
                metricLabel={def.label}
                maxValue={max}
                bins={10}
                valueFormatter={(v) => formatMetricValue(v, def)}
              />
            </Card>
          ))}
        </Grid2>
      </div>
    );
  };

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="flex items-center gap-4 mb-6">
        <h1 className="text-3xl font-bold text-text-primary-light dark:text-text-primary">
          Insights & Comparison
        </h1>
        <DatabaseSelector />
      </div>

      {/* Main Tab Navigation — hidden in client view */}
      {!clientView && <div className="flex overflow-x-auto gap-2 mb-6 border-b border-border">
        <button
          onClick={() => setMainTab('company')}
          className={`px-6 py-3 font-medium transition relative ${
            mainTab === 'company'
              ? 'text-brand-primary'
              : 'text-text-secondary-light dark:text-text-secondary hover:opacity-70'
          }`}
        >
          Company
          {mainTab === 'company' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-primary"></div>
          )}
        </button>

        <button
          onClick={() => setMainTab('industry')}
          className={`px-6 py-3 font-medium transition relative ${
            mainTab === 'industry'
              ? 'text-brand-primary'
              : 'text-text-secondary-light dark:text-text-secondary hover:opacity-70'
          }`}
        >
          Industry
          {mainTab === 'industry' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-primary"></div>
          )}
        </button>

        <button
          onClick={() => setMainTab('village')}
          className={`px-6 py-3 font-medium transition relative ${
            mainTab === 'village'
              ? 'text-brand-primary'
              : 'text-text-secondary-light dark:text-text-secondary hover:opacity-70'
          }`}
        >
          Village
          {mainTab === 'village' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-primary"></div>
          )}
        </button>

        <button
          onClick={() => setMainTab('region')}
          className={`px-6 py-3 font-medium transition relative ${
            mainTab === 'region'
              ? 'text-brand-primary'
              : 'text-text-secondary-light dark:text-text-secondary hover:opacity-70'
          }`}
        >
          Region
          {mainTab === 'region' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-primary"></div>
          )}
        </button>

        <button
          onClick={() => setMainTab('sector')}
          className={`px-6 py-3 font-medium transition relative ${
            mainTab === 'sector'
              ? 'text-brand-primary'
              : 'text-text-secondary-light dark:text-text-secondary hover:opacity-70'
          }`}
        >
          Sector
          {mainTab === 'sector' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-primary"></div>
          )}
        </button>

        <button
          onClick={() => setMainTab('ministry')}
          className={`px-6 py-3 font-medium transition relative ${
            mainTab === 'ministry'
              ? 'text-brand-primary'
              : 'text-text-secondary-light dark:text-text-secondary hover:opacity-70'
          }`}
        >
          Ministry
          {mainTab === 'ministry' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-primary"></div>
          )}
        </button>
      </div>}

      {/* MaxDiff Charts — only shown when a company is selected */}
      {databaseId && selectedCompany && layout?.maxDiffCharts?.length > 0 && (
        <MaxDiffChartsSection
          databaseId={databaseId}
          charts={layout.maxDiffCharts}
          companyId={selectedCompany.id}
        />
      )}

      {/* Company Tab */}
      {(mainTab === 'company' || clientView) && (
        <>
          {/* Company Selection Section */}
          <Card className="mb-6">
        {(!selectedCompany || (!clientView && isDropdownOpen)) ? (
          clientView ? (
            <div className="flex justify-center py-4"><LoadingSpinner /></div>
          ) : (
          <>
            <h2 className="text-xl font-bold text-text-primary-light dark:text-text-primary mb-4">
              Select a Company
            </h2>

            {/* Filter Controls */}
            <Grid3 className="mb-4">
              <div>
                <label className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary mb-2">
                  Search by Name
                </label>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={isLoadingCompanies ? "Loading companies..." : "Type to filter companies..."}
                  disabled={isLoadingCompanies}
                  className={`w-full px-4 py-2 bg-background-light border border-border-light rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-primary focus:border-transparent text-text-primary-light dark:bg-background-gray dark:border-border dark:text-text-primary ${
                    isLoadingCompanies || allCompanies.length <= 0
                      ? "opacity-50 cursor-not-allowed pointer-events-none"
                      : ""
                  }`}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary mb-2">
                  Filter by Industry
                </label>
                <select
                  value={selectedIndustryFilter}
                  onChange={(e) => setSelectedIndustryFilter(e.target.value)}
                  disabled={isLoadingCompanies}
                  className={`w-full px-4 py-2 bg-background-light border border-border-light rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-primary focus:border-transparent text-text-primary-light dark:bg-background-gray dark:border-border dark:text-text-primary ${
                    isLoadingCompanies || allCompanies.length <= 0
                      ? "opacity-50 cursor-not-allowed pointer-events-none"
                      : ""
                  }`}
                >
                  {industries.map((industry) => (
                    <option key={industry} value={industry}>
                      {industry}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary mb-2">
                  Filter by Region
                </label>
                <select
                  value={selectedRegionFilter}
                  onChange={(e) => setSelectedRegionFilter(e.target.value)}
                  disabled={isLoadingCompanies}
                  className={`w-full px-4 py-2 bg-background-light border border-border-light rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-primary focus:border-transparent text-text-primary-light dark:bg-background-gray dark:border-border dark:text-text-primary ${
                    isLoadingCompanies || allCompanies.length <= 0
                      ? "opacity-50 cursor-not-allowed pointer-events-none"
                      : ""
                  }`}
                >
                  {regions.map((region) => (
                    <option key={region} value={region}>
                      {region}
                    </option>
                  ))}
                </select>
              </div>
            </Grid3>

            {/* Company List */}
            <div className="border border-border rounded-lg max-h-96 overflow-y-auto custom-scrollbar">
              {!isLoadingCompanies && allCompanies.length > 0 && (
                <div className="text-sm text-text-muted-light dark:text-text-muted px-4 py-2 bg-background-light border-b border-border-light sticky top-0 z-10 dark:bg-background-gray dark:border-border">
                  {filteredCompanies.length}{" "}
                  {filteredCompanies.length === 1 ? "company" : "companies"}{" "}
                  found
                </div>
              )}
              {isLoadingCompanies ? (
                <div className="px-4 py-8 text-center text-text-secondary-light dark:text-text-secondary">
                  <LoadingSpinner className="mx-auto mt-4 pt-8 pb-8" />
                  <p className="mt-4">Loading companies...</p>
                </div>
              ) : allCompanies.length > 0 ? (
                filteredCompanies.length > 0 ? (
                  filteredCompanies.map((company) => (
                    <div
                      key={company.id}
                      onClick={() => handleSelectCompany(company)}
                      className="px-4 py-3 cursor-pointer border-b border-border last:border-b-0 transition hover:opacity-70"
                    >
                      <div className="font-semibold text-text-primary-light dark:text-text-primary">
                        {company.name}
                      </div>
                      <div className="text-sm text-text-secondary-light dark:text-text-secondary mt-1">
                        {company.industry} • {company.village} •{" "}
                        {company.responseCount} responses
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="px-4 py-8 text-center text-text-secondary-light dark:text-text-secondary">
                    <p>No companies match your filters</p>
                  </div>
                )
              ) : (
                <div className="px-4 py-8 text-center text-text-secondary-light dark:text-text-secondary">
                  <p>No companies available</p>
                </div>
              )}
            </div>
          </>
          )
        ) : (
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="text-sm text-text-secondary-light dark:text-text-secondary mb-1">
                Selected Company
              </div>
              <div className="text-2xl font-bold text-text-primary-light dark:text-text-primary">
                {selectedCompany.name}
              </div>
              <div className="text-sm text-text-muted-light dark:text-text-muted mt-1">
                {selectedCompany.industry} • {selectedCompany.village} •{" "}
                {formatNumber(selectedCompany.responseCount)} responses
              </div>
            </div>

            {!loading && !clientView && (
              <div className="flex gap-3">
                <PDFExport
                  companyData={selectedCompany}
                  comparisonData={comparisonData}
                  type="comparison"
                />
                {permissions?.routes?.includes('/clients') && (
                  <IconButton
                    icon={UserCircleIcon}
                    label="View Client"
                    variant="secondary"
                    size="md"
                    display="icon"
                    onClick={() => navigate(`/clients/${selectedCompany.id}`)}
                  />
                )}
                {permissions?.CMS_access && (
                  <IconButton
                    icon={PencilSquareIcon}
                    label="Edit in CMS"
                    variant="secondary"
                    size="md"
                    display="icon"
                    onClick={() => navigate(`/cms/companies/${selectedCompany.id}`)}
                  />
                )}
                <IconButton
                  icon={ArrowsRightLeftIcon}
                  label="Change Company"
                  variant="secondary"
                  size="md"
                  display="icon"
                  onClick={handleReopen}
                />
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Loading State */}
      {loading && (
        <div className="text-center py-12">
          <LoadingSpinner className="mx-auto mt-4" />
        </div>
      )}

      {/* Error State */}
      {error && (
        <Card variant="danger" className="mb-6">
          <p className="text-danger-dark">{error}</p>
        </Card>
      )}

      {/* Tab Navigation */}
      {!loading &&
        selectedCompany &&
        (allCompaniesComparison || industryComparison) && (
          <>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-text-primary-light dark:text-text-primary mb-4">
                Performance Overview
              </h2>

              {/* Tab Buttons */}
              <div className="flex overflow-x-auto gap-2 border-b border-border">
                <button
                  onClick={() => setActiveTab("all")}
                  className={`px-6 py-3 font-medium transition relative ${
                    activeTab === "all"
                      ? "text-brand-primary"
                      : "text-text-secondary-light dark:text-text-secondary hover:opacity-70"
                  }`}
                >
                  All Companies
                  {activeTab === "all" && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-primary"></div>
                  )}
                </button>
                {selectedCompany.industry && selectedCompany.industry !== 'Unknown' && (
                  <button
                    onClick={() => setActiveTab("industry")}
                    className={`px-6 py-3 font-medium transition relative ${
                      activeTab === "industry"
                        ? "text-brand-primary"
                        : "text-text-secondary-light dark:text-text-secondary hover:opacity-70"
                    }`}
                  >
                    {selectedCompany.industry} Industry
                    {activeTab === "industry" && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-primary"></div>
                    )}
                  </button>
                )}
                {selectedCompany.village && selectedCompany.village !== 'Unknown' && (
                  <button
                    onClick={() => setActiveTab("village")}
                    className={`px-6 py-3 font-medium transition relative ${
                      activeTab === "village"
                        ? "text-brand-primary"
                        : "text-text-secondary-light dark:text-text-secondary hover:opacity-70"
                    }`}
                  >
                    {selectedCompany.village} Village
                    {activeTab === "village" && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-primary"></div>
                    )}
                  </button>
                )}
                {selectedCompany.region && selectedCompany.region !== 'Unknown' && (
                  <button
                    onClick={() => setActiveTab("region")}
                    className={`px-6 py-3 font-medium transition relative ${
                      activeTab === "region"
                        ? "text-brand-primary"
                        : "text-text-secondary-light dark:text-text-secondary hover:opacity-70"
                    }`}
                  >
                    {selectedCompany.region} Region
                    {activeTab === "region" && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-primary"></div>
                    )}
                  </button>
                )}
                <button
                  onClick={() => setActiveTab("sector")}
                  className={`px-6 py-3 font-medium transition relative ${
                    activeTab === "sector"
                      ? "text-brand-primary"
                      : "text-text-secondary-light dark:text-text-secondary hover:opacity-70"
                  }`}
                >
                  {selectedCompany.isGovernment ? 'Government' : 'Non-Government'} Sector
                  {activeTab === "sector" && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-primary"></div>
                  )}
                </button>
                {selectedCompany.isGovernment && selectedCompany.ministry && selectedCompany.ministry !== 'Unknown' && (
                  <button
                    onClick={() => setActiveTab("ministry")}
                    className={`px-6 py-3 font-medium transition relative ${
                      activeTab === "ministry"
                        ? "text-brand-primary"
                        : "text-text-secondary-light dark:text-text-secondary hover:opacity-70"
                    }`}
                  >
                    {selectedCompany.ministry}
                    {activeTab === "ministry" && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-primary"></div>
                    )}
                  </button>
                )}
              </div>
            </div>

            {/* Loading state */}
            {(!comparisonData || !comparisonData.rankings) && (
              <div className="flex justify-center items-center py-12">
                <LoadingSpinner />
              </div>
            )}

            {/* No comparison data available */}
            {comparisonData && comparisonData.rankings && comparisonData.totalCompaniesInComparison === 0 && (
              <div className="bg-background-light dark:bg-surface-dark p-8 rounded-lg border border-border text-center">
                <p className="text-text-secondary-light dark:text-text-secondary">
                  No other companies available for comparison in this category.
                </p>
              </div>
            )}

            {/* Comparison Data Display */}
            {comparisonData && comparisonData.rankings && comparisonData.totalCompaniesInComparison > 0 && (
              <div
                id="comparison-content"
                className={`transition-opacity duration-300 ease-in-out`}
              >
                {/* Comparison Context */}
                {comparisonData && (
                  <p className="text-text-secondary-light dark:text-text-secondary mt-4 mb-6">
                    Comparing against{" "}
                    <span className="font-semibold">
                      {comparisonData.totalCompaniesInComparison}
                    </span>{" "}
                    other {comparisonData.totalCompaniesInComparison === 1 ? "company" : "companies"}{" "}
                    {comparisonData.comparisonSet === "industry" && (
                      <>
                        in the{" "}
                        <span className="font-semibold">
                          {comparisonData.targetCompany.industry}
                        </span>{" "}
                        industry
                      </>
                    )}
                    {comparisonData.comparisonSet === "village" && (
                      <>
                        in{" "}
                        <span className="font-semibold">
                          {comparisonData.targetCompany.village}
                        </span>{" "}
                        village
                      </>
                    )}
                    {comparisonData.comparisonSet === "region" && (
                      <>
                        in the{" "}
                        <span className="font-semibold">
                          {comparisonData.targetCompany.region}
                        </span>{" "}
                        region
                      </>
                    )}
                    {comparisonData.comparisonSet === "sector" && (
                      <>
                        in the{" "}
                        <span className="font-semibold">
                          {comparisonData.targetCompany.isGovernment ? 'Government' : 'Non-Government'}
                        </span>{" "}
                        sector
                      </>
                    )}
                    {comparisonData.comparisonSet === "ministry" && (
                      <>
                        in the{" "}
                        <span className="font-semibold">
                          {comparisonData.targetCompany.ministry}
                        </span>{" "}
                        ministry
                      </>
                    )}
                    {comparisonData.comparisonSet === "all" && (
                      <>
                        across <span className="font-semibold">all industries</span>
                      </>
                    )}
                  </p>
                )}

                {/* Ranking Cards */}
                {isDynamic ? renderDynamicRankingCards() : (
                  <Grid4 className="mb-6">
                    <StatCard
                      title="AI Adoption Rank"
                      value={`#${comparisonData.rankings.adoptionLevel.rank}`}
                      valueColor={getPercentileColor(
                        comparisonData.rankings.adoptionLevel.percentile
                      )}
                      subtitle={
                        <>
                          <span>{getRankLabel(comparisonData.rankings.adoptionLevel)}</span>
                          <ProgressBar
                            value={comparisonData.rankings.adoptionLevel.percentile}
                            colorClass={getBgColorFromTextColor(
                              getPercentileColor(
                                comparisonData.rankings.adoptionLevel.percentile
                              )
                            )}
                          />
                        </>
                      }
                    />

                    <StatCard
                      title="Tech Affinity Rank"
                      value={`#${comparisonData.rankings.atiScore.rank}`}
                      valueColor={getPercentileColor(
                        comparisonData.rankings.atiScore.percentile
                      )}
                      subtitle={
                        <>
                          <span>{getRankLabel(comparisonData.rankings.atiScore)}</span>
                          <ProgressBar
                            value={comparisonData.rankings.atiScore.percentile}
                            colorClass={getBgColorFromTextColor(
                              getPercentileColor(
                                comparisonData.rankings.atiScore.percentile
                              )
                            )}
                          />
                        </>
                      }
                    />

                    <StatCard
                      title="AI Optimism Rank"
                      value={`#${comparisonData.rankings.optimismScore.rank}`}
                      valueColor={getPercentileColor(
                        comparisonData.rankings.optimismScore.percentile
                      )}
                      subtitle={
                        <>
                          <span>{getRankLabel(comparisonData.rankings.optimismScore)}</span>
                          <ProgressBar
                            value={comparisonData.rankings.optimismScore.percentile}
                            colorClass={getBgColorFromTextColor(
                              getPercentileColor(
                                comparisonData.rankings.optimismScore.percentile
                              )
                            )}
                          />
                        </>
                      }
                    />

                    <StatCard
                      title="AI Knowledge Rank"
                      value={`#${comparisonData.rankings.aiKnowledge.rank}`}
                      valueColor={getPercentileColor(
                        comparisonData.rankings.aiKnowledge.percentile
                      )}
                      subtitle={
                        <>
                          <span>{getRankLabel(comparisonData.rankings.aiKnowledge)}</span>
                          <ProgressBar
                            value={comparisonData.rankings.aiKnowledge.percentile}
                            colorClass={getBgColorFromTextColor(
                              getPercentileColor(
                                comparisonData.rankings.aiKnowledge.percentile
                              )
                            )}
                          />
                        </>
                      }
                    />
                  </Grid4>
                )}

                {/* Visual Analytics Charts */}
                {comparisonData && comparisonData.rankings && comparisonData.averages && (
                  isDynamic ? renderDynamicCharts() : (
                    <div className="mb-6" id="insights-content">
                      <h3 className="text-xl font-bold text-text-primary-light dark:text-text-primary mb-4">
                        Visual Analytics
                      </h3>
                      <Grid2>
                        <PercentileBarChart
                          title="Performance Percentiles"
                          data={[
                            { label: 'Adoption Rate', percentile: comparisonData.rankings.adoptionLevel?.percentile || 0, value: selectedCompany.adoptionRate },
                            { label: 'ATI Score', percentile: comparisonData.rankings.atiScore?.percentile || 0, value: selectedCompany.atiScore },
                            { label: 'AI Knowledge', percentile: comparisonData.rankings.aiKnowledge?.percentile || 0, value: selectedCompany.aiKnowledge },
                            { label: 'Optimism', percentile: comparisonData.rankings.optimismScore?.percentile || 0, value: selectedCompany.optimismScore }
                          ]}
                        />
                        <MetricRadarChart
                          title="Metric Comparison"
                          companyLabel={selectedCompany.name}
                          comparisonLabel={activeTab === 'all' ? 'All Companies' : activeTab === 'industry' ? selectedCompany.industry : activeTab === 'village' ? selectedCompany.village : activeTab === 'region' ? selectedCompany.region : 'Average'}
                          data={[
                            { metric: 'Adoption', company: selectedCompany.adoptionLevel || 0, comparison: comparisonData.average.adoptionLevel || 0 },
                            { metric: 'ATI', company: selectedCompany.atiScore || 0, comparison: comparisonData.average.atiScore || 0 },
                            { metric: 'Knowledge', company: (selectedCompany.aiKnowledge || 0) * 20, comparison: (comparisonData.average.aiKnowledge || 0) * 20 },
                            { metric: 'Optimism', company: selectedCompany.optimismScore || 0, comparison: comparisonData.average.optimismScore || 0 },
                            { metric: 'Trust', company: (selectedCompany.aiTrust || 0) * 10, comparison: (comparisonData.average.aiTrust || 0) * 10 },
                            { metric: 'Learning', company: selectedCompany.learningInterest || 0, comparison: comparisonData.average.learningInterest || 0 }
                          ]}
                        />
                      </Grid2>
                    </div>
                  )
                )}

                {/* Distribution Analysis */}
                {isDynamic ? renderDynamicDistributions() : (
                  comparisonData && filteredCompaniesForDistribution.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-xl font-bold text-text-primary-light dark:text-text-primary mb-4">
                        Distribution Analysis
                      </h3>
                      <p className="text-sm text-text-secondary-light dark:text-text-secondary mb-4">
                        See where your company stands in the distribution of all{' '}
                        {activeTab === 'all' ? 'companies' :
                         activeTab === 'industry' ? `companies in ${selectedCompany.industry}` :
                         activeTab === 'village' ? `companies in ${selectedCompany.village}` :
                         activeTab === 'region' ? `companies in ${selectedCompany.region}` :
                         activeTab === 'sector' ? `${selectedCompany.isGovernment ? 'government' : 'non-government'} companies` :
                         activeTab === 'ministry' ? `companies in ${selectedCompany.ministry}` : 'companies'}
                        {' '}({filteredCompaniesForDistribution.length} total)
                      </p>
                      <Grid2 className="gap-6">
                        <Card>
                          <DistributionChart
                            title="Adoption Rate Distribution"
                            companyValue={selectedCompany.metrics?.adoptionRate}
                            allCompanies={filteredCompaniesForDistribution}
                            metricKey="metrics.adoptionRate"
                            metricLabel="Adoption Rate (%)"
                            maxValue={100}
                            bins={10}
                            valueFormatter={(v) => `${v.toFixed(1)}%`}
                          />
                        </Card>
                        <Card>
                          <DistributionChart
                            title="ATI Score Distribution"
                            companyValue={selectedCompany.metrics?.avgATIScore}
                            allCompanies={filteredCompaniesForDistribution}
                            metricKey="metrics.avgATIScore"
                            metricLabel="ATI Score"
                            maxValue={100}
                            bins={10}
                            valueFormatter={(v) => v.toFixed(1)}
                          />
                        </Card>
                        <Card>
                          <DistributionChart
                            title="AI Knowledge Distribution"
                            companyValue={selectedCompany.metrics?.avgAIKnowledge}
                            allCompanies={filteredCompaniesForDistribution}
                            metricKey="metrics.avgAIKnowledge"
                            metricLabel="AI Knowledge"
                            maxValue={5}
                            bins={10}
                            valueFormatter={(v) => v.toFixed(2)}
                          />
                        </Card>
                        <Card>
                          <DistributionChart
                            title="Optimism Score Distribution"
                            companyValue={selectedCompany.metrics?.avgOptimismScore}
                            allCompanies={filteredCompaniesForDistribution}
                            metricKey="metrics.avgOptimismScore"
                            metricLabel="Optimism Score"
                            maxValue={10}
                            bins={10}
                            valueFormatter={(v) => v.toFixed(1)}
                          />
                        </Card>
                      </Grid2>
                    </div>
                  )
                )}

                {/* Detailed Comparison */}
                {isDynamic ? renderDynamicMetricGrids() : (
                  <Grid2 className="mb-6">
                    <Card>
                      <h3 className="text-xl font-bold text-text-primary-light dark:text-text-primary mb-4">
                        AI Adoption & Engagement
                      </h3>
                      <MetricGrid
                        metrics={[
                          {
                            name: (
                              <MetricWithTooltip
                                label="Adoption Level"
                                definition={metricDefinitions.adoptionLevel}
                              />
                            ),
                            value: `${comparisonData.targetCompany.metrics.avgAdoptionLevel?.toFixed(1)}/100`,
                            difference: `${comparisonData.differences.adoptionLevel > 0 ? "+" : ""}${comparisonData.differences.adoptionLevel?.toFixed(1)}`,
                            differenceColor: getPerformanceColor(comparisonData.differences.adoptionLevel),
                          },
                          {
                            name: (
                              <MetricWithTooltip
                                label="Adoption Rate"
                                definition={metricDefinitions.adoptionRate}
                              />
                            ),
                            value: `${comparisonData.targetCompany.metrics.adoptionRate?.toFixed(1)}%`,
                            difference: `${comparisonData.differences.adoptionRate > 0 ? "+" : ""}${comparisonData.differences.adoptionRate?.toFixed(1)}pp`,
                            differenceColor: getPerformanceColor(comparisonData.differences.adoptionRate),
                          },
                          {
                            name: (
                              <MetricWithTooltip
                                label="AI Knowledge"
                                definition={metricDefinitions.aiKnowledge}
                              />
                            ),
                            value: `${comparisonData.targetCompany.metrics.avgAIKnowledge?.toFixed(2)}/5`,
                            difference: `${comparisonData.differences.aiKnowledge > 0 ? "+" : ""}${comparisonData.differences.aiKnowledge?.toFixed(2)}`,
                            differenceColor: getPerformanceColor(comparisonData.differences.aiKnowledge),
                          },
                          {
                            name: (
                              <MetricWithTooltip
                                label="Learning Interest"
                                definition={metricDefinitions.learningInterest}
                              />
                            ),
                            value: `${comparisonData.targetCompany.metrics.interestedInLearning?.toFixed(1)}%`,
                            difference: `${comparisonData.differences.interestedInLearning > 0 ? "+" : ""}${comparisonData.differences.interestedInLearning?.toFixed(1)}pp`,
                            differenceColor: getPerformanceColor(comparisonData.differences.interestedInLearning),
                          },
                        ]}
                      />
                    </Card>

                    <Card>
                      <h3 className="text-xl font-bold text-text-primary-light dark:text-text-primary mb-4">
                        AI Perception & Attitudes
                      </h3>
                      <MetricGrid
                        metrics={[
                          {
                            name: (
                              <MetricWithTooltip
                                label="ATI Score"
                                definition={metricDefinitions.atiScore}
                              />
                            ),
                            value: `${comparisonData.targetCompany.metrics.avgATIScore?.toFixed(1)}/100`,
                            difference: `${comparisonData.differences.atiScore > 0 ? "+" : ""}${comparisonData.differences.atiScore?.toFixed(1)}`,
                            differenceColor: getPerformanceColor(comparisonData.differences.atiScore),
                          },
                          {
                            name: (
                              <MetricWithTooltip
                                label="Optimism Score"
                                definition={metricDefinitions.optimismScore}
                              />
                            ),
                            value: `${comparisonData.targetCompany.metrics.avgOptimismScore?.toFixed(1)}/10`,
                            difference: `${comparisonData.differences.optimismScore > 0 ? "+" : ""}${comparisonData.differences.optimismScore?.toFixed(1)}`,
                            differenceColor: getPerformanceColor(comparisonData.differences.optimismScore),
                          },
                          {
                            name: (
                              <MetricWithTooltip
                                label="AI Trust"
                                definition={metricDefinitions.aiTrust}
                              />
                            ),
                            value: `${comparisonData.targetCompany.metrics.avgTrust?.toFixed(1)}/10`,
                            difference: `${comparisonData.differences.trust > 0 ? "+" : ""}${comparisonData.differences.trust?.toFixed(1)}`,
                            differenceColor: getPerformanceColor(comparisonData.differences.trust),
                          },
                          {
                            name: (
                              <MetricWithTooltip
                                label="AI Fear"
                                definition={metricDefinitions.aiFear}
                              />
                            ),
                            value: `${comparisonData.targetCompany.metrics.avgFear?.toFixed(1)}/10`,
                            difference: `${comparisonData.differences.fear > 0 ? "+" : ""}${comparisonData.differences.fear?.toFixed(1)}`,
                            differenceColor: getPerformanceColor(-comparisonData.differences.fear),
                          },
                        ]}
                      />
                    </Card>
                  </Grid2>
                )}
              </div>
            )}
          </>
        )}
        </>
      )}

      {/* Industry Breakdown Tab */}
      {mainTab === 'industry' && (
        <BreakdownView
          categoryType="industry"
          categories={industryList}
          selectedCategory={selectedIndustry}
          onSelectCategory={setSelectedIndustry}
          breakdownData={industryBreakdown}
          loading={industryLoading}
          error={industryError}
          onCompanyClick={handleCompanyClickFromBreakdown}
          databaseId={databaseId}
          schema={schema}
        />
      )}

      {/* Village Breakdown Tab */}
      {mainTab === 'village' && (
        <BreakdownView
          categoryType="village"
          categories={villageList}
          selectedCategory={selectedVillage}
          onSelectCategory={setSelectedVillage}
          breakdownData={villageBreakdown}
          loading={villageLoading}
          error={villageError}
          onCompanyClick={handleCompanyClickFromBreakdown}
          databaseId={databaseId}
          schema={schema}
        />
      )}

      {/* Region Breakdown Tab */}
      {mainTab === 'region' && (
        <BreakdownView
          categoryType="region"
          categories={regionList}
          selectedCategory={selectedRegion}
          onSelectCategory={setSelectedRegion}
          breakdownData={regionBreakdown}
          loading={regionLoading}
          error={regionError}
          onCompanyClick={handleCompanyClickFromBreakdown}
          databaseId={databaseId}
          schema={schema}
        />
      )}

      {/* Sector Breakdown Tab */}
      {mainTab === 'sector' && (
        <BreakdownView
          categoryType="sector"
          categories={sectorList}
          selectedCategory={selectedSector}
          onSelectCategory={setSelectedSector}
          breakdownData={sectorBreakdown}
          loading={sectorLoading}
          error={sectorError}
          onCompanyClick={handleCompanyClickFromBreakdown}
          databaseId={databaseId}
          schema={schema}
        />
      )}

      {/* Ministry Breakdown Tab */}
      {mainTab === 'ministry' && (
        <BreakdownView
          categoryType="ministry"
          categories={ministryList}
          selectedCategory={selectedMinistry}
          onSelectCategory={setSelectedMinistry}
          breakdownData={ministryBreakdown}
          loading={ministryLoading}
          error={ministryError}
          onCompanyClick={handleCompanyClickFromBreakdown}
          databaseId={databaseId}
          schema={schema}
        />
      )}
    </div>
  );
}
